import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import bgBlue from 'img/nestleLanding/card-bg-blue.png';
import bgBlueMobile from 'img/nestleLanding/card-bg-blue-mobile.png';
import bgGreen from 'img/nestleLanding/card-bg-green.png';
import bgGreenMobile from 'img/nestleLanding/card-bg-green-mobile.png';
import bgPink from 'img/nestleLanding/card-bg-pink.png';
import bgPinkMobile from 'img/nestleLanding/card-bg-pink-mobile.png';
import bgRed from 'img/nestleLanding/card-bg-red.png';
import bgRedMobile from 'img/nestleLanding/card-bg-red-mobile.png';
import React, { type ForwardedRef, type MouseEvent, forwardRef, memo, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import { NESTLE_MOBILE_CLASS } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.helpers';
import { Animal } from 'reactApp/sections/Nestle/PetsCard/Animal/Animal';
import { Breed } from 'reactApp/sections/Nestle/PetsCard/Breed/Breed';
import { Date } from 'reactApp/sections/Nestle/PetsCard/Date/Date';
import { Nickname } from 'reactApp/sections/Nestle/PetsCard/Nickname/Nickname';
import { CardColor } from 'reactApp/sections/Nestle/PetsCard/PetsCard.types';
import { Photo } from 'reactApp/sections/Nestle/PetsCard/Photo/Photo';
import { SecondNickname } from 'reactApp/sections/Nestle/PetsCard/SecondNickname/SecondNickname';
import { Toy } from 'reactApp/sections/Nestle/PetsCard/Toy/Toy';

import styles from './PetsCard.css';

const backgroundImages = {
    [CardColor.blue]: bgBlue,
    [CardColor.green]: bgGreen,
    [CardColor.red]: bgRed,
    [CardColor.pink]: bgPink,
};

const backgroundImagesMobile = {
    [CardColor.blue]: bgBlueMobile,
    [CardColor.green]: bgGreenMobile,
    [CardColor.red]: bgRedMobile,
    [CardColor.pink]: bgPinkMobile,
};

export const PetsCard = memo(
    forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
        const { formData, setFormData } = useContext(NestleFormWrapperContext);
        const { cardColor } = formData;

        const isMobile = useSelector(QuotaLandingSelectors.isMobile);

        const backgroundImage = backgroundImages[cardColor];
        const backgroundImageMobile = backgroundImagesMobile[cardColor];

        const handleChangeColor = useCallback(
            (e: MouseEvent<HTMLDivElement>) => {
                setFormData((prev) => ({
                    ...prev,
                    cardColor: e.currentTarget.getAttribute('data-color') as CardColor,
                }));
            },
            [setFormData]
        );

        return (
            <div className={styles.root}>
                <div
                    className={classNames(styles.cardWrapper, {
                        [styles.cardWrapperMobile]: isMobile,
                    })}
                >
                    <div
                        className={classNames(styles.card, {
                            [styles[`card_${cardColor}`]]: cardColor,
                            // Статичный класс нужен для функционала сохранения мобильный версии карточки.
                            [NESTLE_MOBILE_CLASS]: isMobile,
                        })}
                        ref={ref}
                    >
                        <img src={backgroundImage} alt="card-background" className={styles.cardImage} />
                        <img src={backgroundImageMobile} alt="card-background" className={styles.cardImageMobile} />
                        <Toy ref={ref} />
                        <div className={styles.form}>
                            <Photo />
                            <div className={styles.fields}>
                                <Nickname />
                                <SecondNickname />
                                <Animal />
                                <Breed />
                                <Date />
                            </div>
                        </div>
                    </div>
                </div>
                <Spacing size={isMobile ? 12 : 30} />
                <div
                    className={classNames(styles.colorSelector, {
                        [styles.colorSelectorMobile]: isMobile,
                    })}
                >
                    <Text className={styles.colorSelectorTitle}>Выберите дизайн для карточки</Text>
                    {Object.keys(CardColor).map((color) => {
                        return (
                            <div
                                role="button"
                                aria-label={color}
                                key={color}
                                data-color={color}
                                className={classNames(styles.colorButton, {
                                    [styles.colorButtonActive]: color === cardColor,
                                    [styles[`colorButton_${color}`]]: color,
                                })}
                                onClick={handleChangeColor}
                            />
                        );
                    })}
                </div>
                <Spacing size={isMobile ? 12 : 30} />
            </div>
        );
    })
);
